import { Scanner } from '@yudiel/react-qr-scanner';
import { useState, useEffect, useRef } from 'react';
import { getFirestore, collection, addDoc, query, where, getDocs, getDoc, orderBy, Timestamp, updateDoc, doc } from 'firebase/firestore';
import { Modal, Form, Row, Col, InputGroup, Spinner } from "react-bootstrap";
import { toast } from 'react-toastify';
import {
  FaSortAlphaDown, FaRegClock, FaUserFriends, FaInfoCircle,
  FaTimesCircle, FaCheckCircle, FaBan, FaFilter,
  FaDownload, FaUserPlus, FaExclamationCircle, FaExclamationTriangle, FaUser
} from 'react-icons/fa';

import './xDocList.css';

// --- DEVELOPER CONFIGURATION ---
const ADMIN_PASSCODE = process.env.REACT_APP_ADMIN_PASSCODE || "1234";
// -------------------------------

const QRWebAndNativeScan = () => {
  const [scannedResult, setScannedResult] = useState('');
  const [isPaused, setIsPaused] = useState(false);

  // Modals
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: '', name: '', time: '', type: '', variant: 'success',
    subtext: '', isFlagged: false, hasIncident: false, isBanned: false, email: ''
  });
  const [selectedLog, setSelectedLog] = useState(null);

  // Guest Logic State
  const [guestNameInput, setGuestNameInput] = useState('');
  const [addingGuest, setAddingGuest] = useState(false);
  const [lastMemberName, setLastMemberName] = useState('');
  const [lastMemberEmail, setLastMemberEmail] = useState('');
  const [guestItems, setGuestItems] = useState([]);

  // List & Search State
  const [recentLogs, setRecentLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterExpiredOnly, setFilterExpiredOnly] = useState(false);
  const [filterFlaggedOnly, setFilterFlaggedOnly] = useState(false);
  const [filterIncidentOnly, setFilterIncidentOnly] = useState(false);
  const [filterSuccessOnly, setFilterSuccessOnly] = useState(false);
  const [filterBypassOnly, setFilterBypassOnly] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const [sortConfig, setSortConfig] = useState({ key: 'scannedAt', direction: 'desc' });
  const [manualMode, setManualMode] = useState(false);
  const [historyMode, setHistoryMode] = useState(false);
  const [isAdminAuth, setIsAdminAuth] = useState(true);

  const todayStr = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);

  const [passcodeAttempt, setPasscodeAttempt] = useState('');
  const [manualInput, setManualInput] = useState('');
  const [loading, setLoading] = useState(false);

  const db = getFirestore();

  const getLogsCollectionName = (date = new Date()) => {
    const year = date instanceof Date ? date.getFullYear() : new Date().getFullYear();
    return `access_logs_${year}`;
  };

  const formatDisplayValue = (str) => {
    if (!str) return "";
    if (str.includes('@')) return str.toLowerCase();
    return str.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  // --- CORE DATA FUNCTIONS ---

  const fetchLogs = async (isTodayOnly = true) => {
    setLoadingLogs(true);
    try {
      let q;
      const logsRef = collection(db, getLogsCollectionName(isTodayOnly ? new Date() : new Date(startDate)));
      if (isTodayOnly) {
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        q = query(logsRef, where("scannedAt", ">=", Timestamp.fromDate(startOfToday)), orderBy("scannedAt", "desc"));
      } else {
        const start = new Date(startDate + "T00:00:00");
        const end = new Date(endDate + "T23:59:59");
        q = query(logsRef, where("scannedAt", ">=", Timestamp.fromDate(start)), where("scannedAt", "<=", Timestamp.fromDate(end)), orderBy("scannedAt", "desc"));
      }

      const querySnapshot = await getDocs(q);
      const logs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), guestCount: 0 }));

      setRecentLogs(logs);

      // Background progressive calculation of guest metrics
      logs.forEach(async (log) => {
        try {
          let updatedSponsorName = log.sponsorName || "";
          let totalGuests = 0;
          let attachedEmail = log.rawPayload || "";

          if (log.entryType === 'Guest' && log.signedInBy && log.signedInBy.includes('@')) {
            const pQuery = query(collection(db, "profiles"), where("email", "==", log.signedInBy.toLowerCase()));
            const pSnap = await getDocs(pQuery);
            if (!pSnap.empty) updatedSponsorName = pSnap.docs[0].data().name;
          }

          if ((log.entryType === 'Member' || !log.entryType) && attachedEmail && attachedEmail.trim() !== "") {
            const activeGuestsRef = collection(db, getLogsCollectionName());
            const startOfToday = new Date();
            startOfToday.setHours(0, 0, 0, 0);

            const guestQ = query(
              activeGuestsRef,
              where("signedInBy", "==", attachedEmail.toLowerCase()),
              where("entryType", "==", "Guest"),
              where("scannedAt", ">=", Timestamp.fromDate(startOfToday))
            );
            const guestSnap = await getDocs(guestQ);
            totalGuests = guestSnap.docs.map(d => d.data()).filter(g => g.status !== 'cancelled').length;
          }

          if (totalGuests > 0 || updatedSponsorName !== "") {
            setRecentLogs(prevLogs =>
              prevLogs.map(item =>
                item.id === log.id ? { ...item, sponsorName: updatedSponsorName, guestCount: totalGuests } : item
              )
            );
          }
        } catch (innerErr) {
          console.warn("Background enrichment pass skipped:", log.id, innerErr);
        }
      });

    } catch (err) {
      console.error("Main fetchLogs error:", err);
      toast.error("Error loading access logs");
    } finally {
      setLoadingLogs(false);
    }
  };

  const fetchGuestsForMember = async (email) => {
    if (!email) return;
    const logsRef = collection(db, getLogsCollectionName());
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const q = query(logsRef, where("signedInBy", "==", email.toLowerCase()), where("entryType", "==", "Guest"), where("scannedAt", ">=", Timestamp.fromDate(startOfToday)), orderBy("scannedAt", "desc"));
    const snap = await getDocs(q);
    setGuestItems(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(g => g.status !== 'cancelled'));
  };

  useEffect(() => { if (isAdminAuth) fetchLogs(!historyMode); }, [isAdminAuth, historyMode]);

  // --- LOGIC FUNCTIONS ---

  async function logVisit(name, method, type, raw = "", sponsor = "", expired = false, flagged = false, incident = false, isBanned = false, bypassReason = "") {
    const timestamp = new Date();
    try {
      await addDoc(collection(db, getLogsCollectionName(timestamp)), {
        fullName: name,
        status: bypassReason ? 'bypassed' : 'active',
        entryType: type, method, rawPayload: raw, signedInBy: sponsor,
        isExpired: expired, isFlagged: flagged, hasIncident: incident,
        isBanned, bypassReason, scannedAt: timestamp
      });
      fetchLogs(!historyMode);
      return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (err) { return new Date().toLocaleTimeString(); }
  }

  async function verifyMemberProfile(identifier) {
    const cleaned = identifier.trim().toLowerCase();
    const isEmail = /\S+@\S+\.\S+/.test(cleaned);
    const field = isEmail ? 'email' : 'name';
    const q = query(collection(db, "profiles"), where(field, "==", isEmail ? cleaned : identifier.trim()));
    const snap = await getDocs(q);
    let memberData = null;

    if (!snap.empty) {
      const data = snap.docs[0].data();
      let isExp = false; let dateS = "N/A";
      let isFlagged = false;
      if (data.expirationDate) {
        const expD = data.expirationDate.toDate ? data.expirationDate.toDate() : new Date(data.expirationDate);
        dateS = expD.toLocaleDateString();
        if (expD < new Date()) isExp = true;
      }
      if (data.type) {
        const levelDocRef = doc(db, "membership_levels", data.type);
        const levelSnap = await getDoc(levelDocRef);
        if (levelSnap.exists() && levelSnap.data().account_flag === true) isFlagged = true;
      }
      memberData = { name: data.name, expired: isExp, expiryDate: dateS, email: data.email || data.rawPayload || "", flagged: isFlagged, incident: data.hasIncident === true };
    }

    const searchName = memberData ? memberData.name : identifier.trim();
    const names = searchName.split(' ');
    const firstName = names[0] || '';
    const lastName = names.slice(1).join(' ') || '';
    const banQ = query(collection(db, "banned_list"), where("firstName", "==", firstName), where("lastName", "==", lastName));
    const banSnap = await getDocs(banQ);

    if (!banSnap.empty) {
      const banInfo = banSnap.docs[0].data();
      return { ...(memberData || { name: searchName, email: identifier }), isBanned: true, banReason: banInfo.reason };
    }
    return memberData;
  }

  // --- UI EVENT HANDLERS ---

  const handleBypass = async () => {
    const code = window.prompt("SECURITY OVERRIDE: Enter Admin Passcode to continue:");
    if (code !== ADMIN_PASSCODE) return toast.error("Unauthorized: Invalid Passcode");

    const reason = window.prompt("Passcode Accepted. Enter reason for bypass (REQUIRED):");
    if (!reason || reason.trim().length < 3) return toast.warn("Bypass cancelled: Reason is required.");

    await logVisit(modalContent.name, "Manual Bypass", modalContent.type, modalContent.email || "", lastMemberEmail || "Admin", false, false, false, false, reason);
    toast.success("Entry Bypassed & Logged");
    handleCloseModal();
  };

  const handleQuickGuestAdd = async () => {
    const guestName = guestNameInput.trim();
    if (!guestName) return toast.error("Enter guest name");

    if (!lastMemberEmail || lastMemberEmail.trim() === "") {
      return toast.error("Guest addition failed: Missing sponsor synchronization link.");
    }

    setAddingGuest(true);
    try {
      const guestCheck = await verifyMemberProfile(guestName);
      if (guestCheck && guestCheck.isBanned) {
        await logVisit(guestName, "Attempt", "Guest", lastMemberEmail, lastMemberEmail, false, false, false, true, "");
        setModalContent({ title: "Access Denied", name: guestName, variant: 'danger', isBanned: true, subtext: `The person you are trying to add is on a banned list.`, type: "Guest", email: "" });
        setShowModal(true);
        return;
      }
      await logVisit(guestName, "Quick Add", "Guest", lastMemberEmail, lastMemberEmail);
      toast.success(`Guest added: ${formatDisplayValue(guestName)}`);
      setGuestNameInput('');

      // FIX: Force dynamic guest items sync immediately so they render inside the active view
      if (lastMemberEmail) fetchGuestsForMember(lastMemberEmail);
    } finally { setAddingGuest(false); }
  };

  const handleScan = (result) => {
    if (result && result.length > 0 && !scannedResult) {
      const raw = result[0].rawValue; setScannedResult(raw); setIsPaused(true);
      verifyMemberProfile(raw).then(member => {
        if (member) {
          logVisit(member.name, "QR Scan", "Member", member.email || raw, "", member.expired, member.flagged, member.incident, member.isBanned).then(t => {
            const isDenied = member.expired || member.flagged || member.incident || member.isBanned;

            if (!isDenied) {
              const verifiedEmail = member.email || raw;
              if (verifiedEmail && verifiedEmail.includes('@')) {
                setLastMemberName(member.name);
                setLastMemberEmail(verifiedEmail.toLowerCase());
                fetchGuestsForMember(verifiedEmail.toLowerCase());
              }
            }

            setModalContent({
              title: isDenied ? "Access Denied" : "Check-in Successful", name: member.name, email: member.email || raw, time: t, type: "Member",
              variant: isDenied ? 'danger' : 'success', isFlagged: member.flagged, hasIncident: member.incident, isBanned: member.isBanned,
              subtext: member.isBanned ? `BANNED: ${member.banReason}` : (member.flagged ? "Flagged Account" : (member.incident ? "Open Incident" : (member.expired ? `Expired: ${member.expiryDate}` : "")))
            });
            setShowModal(true);
          });
        } else {
          setModalContent({ title: "Access Denied", name: "Invalid QR", variant: 'danger', isBanned: false });
          setShowModal(true);
        }
      });
    }
  };

  const handleManualSubmit = async () => {
    const input = manualInput.trim();
    if (!input) return toast.error("Enter name/email");
    setLoading(true);
    try {
      const member = await verifyMemberProfile(input);
      if (member) {
        const isDenied = member.expired || member.flagged || member.incident || member.isBanned;

        const resolvedEmail = member.email || (input.includes('@') ? input : "");

        if (!isDenied && resolvedEmail !== "") {
          setLastMemberName(member.name);
          setLastMemberEmail(resolvedEmail.toLowerCase());
          fetchGuestsForMember(resolvedEmail.toLowerCase());
        }

        const t = await logVisit(member.name, "Manual", "Member", resolvedEmail, "", member.expired, member.flagged, member.incident, member.isBanned);

        setModalContent({
          title: isDenied ? "Access Denied" : "Check-in Successful", name: member.name, email: resolvedEmail, time: t, type: "Member",
          variant: isDenied ? 'danger' : 'success', isFlagged: member.flagged, hasIncident: member.incident, isBanned: member.isBanned,
          subtext: member.isBanned ? `BANNED: ${member.banReason}` : (member.flagged ? "Flagged" : (member.incident ? "Incident" : (member.expired ? `Expired: ${member.expiryDate}` : "")))
        });

        if (!isDenied) { setManualInput(''); setManualMode(false); }
        setShowModal(true);
      } else { toast.error("Not found"); }
    } finally { setLoading(false); }
  };

  // --- FILTERS & SORT UI ---

  const handleToggleExpired = (v) => { setFilterExpiredOnly(v); if (v) { setFilterFlaggedOnly(false); setFilterIncidentOnly(false); setFilterSuccessOnly(false); setFilterBypassOnly(false); } };
  const handleToggleFlagged = (v) => { setFilterFlaggedOnly(v); if (v) { setFilterExpiredOnly(false); setFilterIncidentOnly(false); setFilterSuccessOnly(false); setFilterBypassOnly(false); } };
  const handleToggleIncident = (v) => { setFilterIncidentOnly(v); if (v) { setFilterExpiredOnly(false); setFilterFlaggedOnly(false); setFilterSuccessOnly(false); setFilterBypassOnly(false); } };
  const handleToggleSuccess = (v) => { setFilterSuccessOnly(v); if (v) { setFilterExpiredOnly(false); setFilterFlaggedOnly(false); setFilterIncidentOnly(false); setFilterBypassOnly(false); } };
  const handleToggleBypass = (v) => { setFilterBypassOnly(v); if (v) { setFilterExpiredOnly(false); setFilterFlaggedOnly(false); setFilterIncidentOnly(false); setFilterSuccessOnly(false); } };

  const clearAllFilters = () => { setFilterExpiredOnly(false); setFilterFlaggedOnly(false); setFilterIncidentOnly(false); setFilterSuccessOnly(false); setFilterBypassOnly(false); setSearchTerm(''); };

  const toggleSort = (key) => {
    let direction = sortConfig.key === key && sortConfig.direction === 'desc' ? 'asc' : 'desc';
    setSortConfig({ key, direction });
  };

  const filteredLogsList = recentLogs
    .filter(log => {
      const matchesSearch = log.fullName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesExpired = filterExpiredOnly ? log.isExpired === true : true;
      const matchesFlagged = filterFlaggedOnly ? log.isFlagged === true : true;
      const matchesIncident = filterIncidentOnly ? log.hasIncident === true : true;
      const matchesBypass = filterBypassOnly ? log.status === 'bypassed' : true;
      const matchesSuccess = filterSuccessOnly ? (log.status === 'active' && !log.isExpired && !log.isFlagged && !log.hasIncident && !log.isBanned) : true;
      return matchesSearch && matchesExpired && matchesFlagged && matchesIncident && matchesSuccess && matchesBypass;
    })
    .sort((a, b) => {
      let valA = a[sortConfig.key]; let valB = b[sortConfig.key];
      if (sortConfig.key === 'scannedAt') { valA = a.scannedAt?.seconds || 0; valB = b.scannedAt?.seconds || 0; }
      else if (typeof valA === 'string') { valA = valA.toLowerCase(); valB = valB.toLowerCase(); }
      return sortConfig.direction === 'asc' ? (valA > valB ? 1 : -1) : (valA < valB ? 1 : -1);
    });

  const logStats = {
    total: recentLogs.length,
    success: recentLogs.filter(l => l.status === 'active' && !l.isExpired && !l.isFlagged && !l.hasIncident && !l.isBanned).length,
    bypassed: recentLogs.filter(l => l.status === 'bypassed').length
  };

  const handleCloseModal = () => {
    setShowModal(false); setScannedResult(''); setGuestNameInput('');
    setLastMemberName(''); setLastMemberEmail(''); setGuestItems([]); setIsPaused(false);
  };

  const downloadCSV = () => {
    if (filteredLogsList.length === 0) return toast.info("No data to download");
    const headers = ["Name", "Type", "Method", "Sponsor", "Status", "Time", "Bypass Reason"];
    const rows = filteredLogsList.map(log => {
      const time = log.scannedAt?.toDate()?.toLocaleString() || '--';
      return [`"${log.fullName}"`, `"${log.entryType}"`, `"${log.method}"`, `"${log.signedInBy || ''}"`, `"${log.status}"`, `"${time}"`, `"${log.bypassReason || ''}"`];
    });
    const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = `Access_Logs.csv`;
    link.click();
  };

  return (
    <div className="qr-scanner-page">
      {!isAdminAuth ? (
        <div className="p-4 qr-modal-content shadow-lg mt-5 mx-auto" style={{ maxWidth: '420px' }}>
          <div className="qr-modal-body-container text-center">
            <h4 className="text-white mb-4 fw-bold text-uppercase text-start" style={{ letterSpacing: '1px', fontSize: '14px' }}>Station Setup</h4>
            <Form.Control type="password" placeholder="Passcode" value={passcodeAttempt} onChange={(e) => setPasscodeAttempt(e.target.value)} className="qr-input mb-4 text-center" onKeyPress={(e) => e.key === 'Enter' && (passcodeAttempt === ADMIN_PASSCODE ? setIsAdminAuth(true) : toast.error("Invalid Passcode"))} />
            <button className="qr-btn qr-btn-primary w-100" onClick={() => passcodeAttempt === ADMIN_PASSCODE ? setIsAdminAuth(true) : toast.error("Invalid Passcode")}>UNLOCK STATION</button>
          </div>
        </div>
      ) : (
        <>
          {!manualMode ? (
            <>
              <div className="mb-3 mt-2"><h5 className="qr-header-info">READY TO SCAN</h5><small className="qr-subtext">Place QR code or ID card in front of camera</small></div>
              <div className="qr-video-container mb-3 border border-secondary rounded overflow-hidden shadow-sm pos-relative outer-scanner-frame">
                <Scanner
                  onScan={handleScan}
                  paused={isPaused}
                  formats={['qr_code', 'pdf417']}
                  styles={{ container: { height: '100%', width: '100%' }, video: { objectFit: 'cover' } }}
                />
                <div className="custom-scanner-overlay">
                  <div className="scanner-square-finder">
                    <div className="corner-bracket tl"></div>
                    <div className="corner-bracket tr"></div>
                    <div className="corner-bracket bl"></div>
                    <div className="corner-bracket br"></div>
                    <div className="scanner-laser-line"></div>
                  </div>
                </div>
              </div>
              <br />
              <Row className="g-2 mb-4 align-items-center">
                <Col xs={8}><button className="qr-btn qr-btn-warning w-100" onClick={() => setManualMode(true)}>Manual Sign In</button></Col>
                <Col xs={2} className="d-flex justify-content-center"><button onClick={() => setHistoryMode(!historyMode)} className={`qr-icon-btn ${historyMode ? 'active' : ''}`}><FaFilter size={18} /></button></Col>
                <Col xs={2} className="d-flex justify-content-end"><button className="qr-icon-btn" style={{ color: 'var(--brand-danger)' }} onClick={() => setIsAdminAuth(false)}>🔒</button></Col>
              </Row>

              {historyMode && (
                <div className="qr-table-container p-3 mb-3 text-start">
                  <Row className="g-2 align-items-end mb-3">
                    <Col xs={12} md={4}><label className="qr-label">Start</label><Form.Control type="date" size="sm" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="qr-input" /></Col>
                    <Col xs={12} md={4}><label className="qr-label">End</label><Form.Control type="date" size="sm" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="qr-input" /></Col>
                    <Col xs={6} md={2}><button className="qr-btn qr-btn-primary w-100 py-1" style={{ fontSize: '0.8rem' }} onClick={() => fetchLogs(false)}>GO</button></Col>
                    <Col xs={6} md={2}><button className="qr-btn qr-btn-warning w-100 py-1" style={{ fontSize: '0.8rem' }} onClick={downloadCSV}><FaDownload size={18} /></button></Col>
                  </Row>
                  <div className="d-flex justify-content-between align-items-center p-2 rounded flex-wrap gap-2" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <div className="d-flex gap-3 flex-wrap">
                      <Form.Check type="switch" label={<span className="small text-danger fw-bold">EXPIRED</span>} checked={filterExpiredOnly} onChange={(e) => handleToggleExpired(e.target.checked)} />
                      <Form.Check type="switch" label={<span className="small text-warning fw-bold">FLAGGED</span>} checked={filterFlaggedOnly} onChange={(e) => handleToggleFlagged(e.target.checked)} />
                      <Form.Check type="switch" label={<span className="small text-info fw-bold">INCIDENT</span>} checked={filterIncidentOnly} onChange={(e) => handleToggleIncident(e.target.checked)} />
                      <Form.Check type="switch" label={<span className="small text-success fw-bold">SUCCESS</span>} checked={filterSuccessOnly} onChange={(e) => handleToggleSuccess(e.target.checked)} />
                      <Form.Check type="switch" label={<span className="small text-primary fw-bold">BYPASS</span>} checked={filterBypassOnly} onChange={(e) => handleToggleBypass(e.target.checked)} />
                    </div>
                    <div className="small text-white-50">
                      Total: <span className="text-white fw-bold">{logStats.total}</span> | Success: <span className="text-success fw-bold">{logStats.success}</span> | Bypass: <span className="text-primary fw-bold">{logStats.bypassed}</span>
                      <button className="btn btn-link btn-sm text-white p-0 ms-2" style={{ fontSize: '0.65rem', textDecoration: 'none' }} onClick={clearAllFilters}>[RESET]</button>
                    </div>
                  </div>
                </div>
              )}

              <div className="text-start">
                <InputGroup className="mb-2 shadow-sm">
                  <Form.Control placeholder="🔎 Filter names..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="glass-search" />
                </InputGroup>

                <div className="qr-card-list">
                  {filteredLogsList.map((log) => {
                    const isMember = log.entryType === 'Member' || !log.entryType;
                    const isDenied = log.isExpired || log.isFlagged || log.hasIncident || log.isBanned;

                    return (
                      <div
                        key={log.id}
                        onClick={() => {
                          if (!isMember) return;

                          setSelectedLog({
                            ...log,
                            entryType: log.entryType || 'Member',
                            status: log.status || 'active',
                            isExpired: log.isExpired || false,
                            isFlagged: log.isFlagged || false,
                            hasIncident: log.hasIncident || false,
                            isBanned: log.isBanned || false
                          });
                          setShowDetailModal(true);
                        }}
                        className={`qr-log-card ${log.status === 'cancelled' ? 'opacity-50' : ''}`}
                        style={{
                          cursor: isMember ? 'pointer' : 'default',
                          transform: isMember ? undefined : 'none'
                        }}
                      >
                        <div className="qr-card-content align-items-center">
                          <div className="qr-card-main-info">
                            <div className="qr-card-name fw-bold">
                              {formatDisplayValue(log.fullName)}
                              {log.isBanned ? <FaBan className="ms-2 text-danger" title="Banned" /> : log.isFlagged ? <FaBan className="ms-2 text-warning" title="Flagged" /> : log.hasIncident ? <FaExclamationTriangle className="ms-2 text-danger" title="Incident" /> : log.isExpired && <FaExclamationCircle className="ms-2 text-danger" title="Expired" />}
                            </div>
                            <div className="qr-card-meta">
                              <span className={`qr-badge ps-0 ms-0 ${log.entryType === 'Guest' ? 'qr-badge-guest' : (log.isExpired || log.isFlagged || log.hasIncident || log.isBanned ? 'qr-badge-expired' : 'qr-badge-member')}`}>
                                {log.entryType === 'Guest' ? <FaUserFriends size={10} className="me-1" /> : <FaUser size={10} className="me-1" />}
                                {log.entryType || 'Member'}
                              </span>
                              {log.entryType === 'Guest' && log.signedInBy && <span className="ms-2 qr-subtext-small opacity-50">Sponsor: {formatDisplayValue(log.sponsorName || log.signedInBy)}</span>}
                              {isMember && log.guestCount > 0 && (
                                <span className="ms-2 badge rounded-pill bg-danger" style={{ border: '1px solid white', fontSize: '0.7rem' }}>
                                  {log.guestCount} {log.guestCount === 1 ? 'Guest' : 'Guests'}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Active Row Interventions Button Tray */}
                          <div className="action-tray-container" onClick={(e) => e.stopPropagation()}>
                            {!isMember ? (
                              <button 
                                className="action-circle-btn ban-btn" 
                                title="Create Banned Document"
                                onClick={() => alert("Create Banned Doc Placeholder")}
                              >
                                <FaBan size={12} />
                              </button>
                            ) : (
                              <>
                                {/* Only show manage guest option if account is in clean standing */}
                                {!isDenied && (
                                  <button 
                                    className="action-circle-btn guest-btn" 
                                    title="Manage Guests"
                                    onClick={async () => {
                                      let memberEmail = log.rawPayload;
                                      if (memberEmail && !memberEmail.includes('@')) {
                                        const pQuery = query(collection(db, "profiles"), where("name", "==", log.fullName));
                                        const pSnap = await getDocs(pQuery);
                                        if (!pSnap.empty) {
                                          memberEmail = pSnap.docs[0].data().email;
                                        }
                                      }
                                      if (memberEmail) {
                                        setLastMemberEmail(memberEmail.toLowerCase());
                                        setLastMemberName(log.fullName);
                                        await fetchGuestsForMember(memberEmail);
                                        setShowGuestModal(true);
                                      } else {
                                        toast.error("Could not link guest profile: Missing member email anchor.");
                                      }
                                    }}
                                  >
                                    <FaUserPlus size={12} />
                                  </button>
                                )}
                                <button 
                                  className="action-circle-btn incident-btn" 
                                  title="Create Incident Document"
                                  onClick={() => alert("Create Incident Doc Placeholder")}
                                >
                                  <FaExclamationTriangle size={12} />
                                </button>
                              </>
                            )}
                          </div>

                          <div className="qr-card-time-wrap">
                            <div className="qr-time-display fw-bold text-end">
                              {log.status === 'bypassed' && <div className="text-info small" style={{ fontSize: '0.65rem' }}>BYPASS</div>}
                              {log.scannedAt?.toDate ? log.scannedAt.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--'}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          ) : (
            <div className="qr-table-container shadow-lg mt-3 p-4">
              <h4 className="text-white fw-bold m-0 text-uppercase">Manual Sign In</h4>
              <Form.Group className="mb-3 text-start mt-3"><label className="qr-label">MEMBER NAME OR EMAIL</label><Form.Control type="text" value={manualInput} onChange={(e) => setManualInput(e.target.value)} className="qr-input" /></Form.Group>
              <button className="qr-btn qr-btn-primary w-100" onClick={handleManualSubmit}>VERIFY MEMBER</button>
              <button className="qr-btn qr-btn-warning w-100 mt-2" onClick={() => setManualMode(false)}>RETURN</button>
            </div>
          )}
        </>
      )}

      {/* --- RESULT MODAL WITH INTEGRATED REAL-TIME GUEST PREVIEW LIST --- */}
      <Modal show={showModal} onHide={handleCloseModal} centered contentClassName="qr-modal-content" backdropClassName='bg-dark'>
        <div className="qr-modal-success-bar" style={{ background: modalContent.variant === 'danger' ? 'var(--brand-secondary)' : 'var(--brand-primary)' }}></div>
        <Modal.Body className="p-0 text-center">
          <div className="qr-modal-body-container">
            <div className="mb-2" style={{ fontSize: '3.5rem', color: modalContent.variant === 'danger' ? 'var(--brand-secondary)' : 'var(--brand-primary)' }}>
              {modalContent.variant === 'danger' ? <FaTimesCircle /> : <FaCheckCircle />}
            </div>
            <h2 className="mb-1 fw-bold">{formatDisplayValue(modalContent.name)}</h2>
            <div className={`qr-badge mb-3 d-inline-block px-4 py-2 ${modalContent.variant === 'danger' ? 'qr-badge-expired' : 'qr-badge-member'}`}>
              {modalContent.isBanned ? 'BANNED' : (modalContent.isFlagged ? 'FLAGGED' : (modalContent.hasIncident ? 'INCIDENT' : (modalContent.variant === 'danger' ? 'EXPIRED' : modalContent.type)))}
            </div>
            {modalContent.subtext && <div className="p-3 mb-3 rounded qr-subtext-warning"><h6 className="m-0 small fw-bold text-warning">{modalContent.subtext}</h6></div>}
            {modalContent.variant === 'danger' && (<div className="mt-3 p-3 rounded" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}><p className="small text-white-50 mb-2">Security override required?</p><button className="qr-btn qr-btn-primary w-100 py-2" onClick={handleBypass}>AUTHORIZE BYPASS</button></div>)}

            {modalContent.variant === 'success' && !modalContent.isFlagged && !modalContent.hasIncident && !modalContent.isBanned && lastMemberEmail && (
              <div className="qr-guest-section border-top border-secondary pt-3 mt-3 text-start">
                <label className="qr-label mb-2 d-block">Bringing a guest?</label>
                <InputGroup className="mb-3">
                  <Form.Control placeholder="Guest Name..." className="qr-input" value={guestNameInput} onChange={(e) => setGuestNameInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleQuickGuestAdd()} />
                  <button className="qr-btn qr-btn-primary px-3" onClick={handleQuickGuestAdd}><FaUserPlus size={18} /></button>
                </InputGroup>

                {/* FIX: Live embedded viewport renders newly added guest arrays directly inside the active checkout pop-up frame */}
                <div className="qr-card-list custom-scroll" style={{ maxHeight: '140px', overflowY: 'auto' }}>
                  {guestItems.map((guest) => (
                    <div key={guest.id} className="qr-log-card py-2 px-3 d-flex justify-content-between align-items-center mb-1" style={{ background: 'rgba(255,255,255,0.03)' }}>
                      <div className="fw-bold small text-white">{formatDisplayValue(guest.fullName)}</div>
                      <button className="btn btn-sm text-danger fw-bold border-0 bg-transparent p-0" onClick={() => updateDoc(doc(db, getLogsCollectionName(), guest.id), { status: 'cancelled' }).then(() => fetchGuestsForMember(lastMemberEmail))}>REMOVE</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <button className="qr-btn qr-btn-warning qr-btn-finish w-100 mt-3" onClick={handleCloseModal}>Finish</button>
          </div>
        </Modal.Body>
      </Modal>

      {/* --- ENTRY DETAILS MODAL --- */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered contentClassName="qr-modal-content" backdropClassName='bg-dark'>
        <div className="qr-modal-success-bar" style={{ background: (selectedLog?.isExpired || selectedLog?.isFlagged || selectedLog?.hasIncident || selectedLog?.isBanned) ? 'var(--brand-secondary)' : 'var(--brand-info)' }}></div>
        <Modal.Body className="p-4 text-white">
          {selectedLog && (
            <div className="text-start">
              <h3 className="fw-bold mb-4 text-center">{formatDisplayValue(selectedLog.fullName)}</h3>
              <Row className="mb-2"><Col xs={5} className="small text-uppercase fw-bold opacity-50">Status:</Col>
                <Col className={`small fw-bold ${(selectedLog.isExpired || selectedLog.isFlagged || selectedLog.hasIncident || selectedLog.isBanned) ? "text-danger" : "text-success"}`}>
                  {selectedLog.isBanned ? "BANNED" : selectedLog.isFlagged ? "FLAGGED" : selectedLog.hasIncident ? "INCIDENT" : selectedLog.isExpired ? "EXPIRED" : "SUCCESS"}
                </Col>
              </Row>
              {selectedLog.bypassReason && <Row className="mb-2"><Col xs={5} className="small text-uppercase fw-bold opacity-50">Reason:</Col><Col className="small text-warning italic">{selectedLog.bypassReason}</Col></Row>}
              <Row className="mb-2"><Col xs={5} className="small text-uppercase fw-bold opacity-50">Time:</Col><Col className="small">{selectedLog.scannedAt?.toDate()?.toLocaleString()}</Col></Row>

              {selectedLog.status === 'active' && !selectedLog.isExpired && !selectedLog.isFlagged && !selectedLog.hasIncident && !selectedLog.isBanned && (
                <button
                  className="qr-btn qr-btn-primary w-100 mt-4"
                  onClick={async () => {
                    let memberEmail = selectedLog.rawPayload;

                    if (memberEmail && !memberEmail.includes('@')) {
                      const pQuery = query(collection(db, "profiles"), where("name", "==", selectedLog.fullName));
                      const pSnap = await getDocs(pQuery);
                      if (!pSnap.empty) {
                        memberEmail = pSnap.docs[0].data().email;
                      }
                    }

                    if (memberEmail) {
                      setLastMemberEmail(memberEmail.toLowerCase());
                      setLastMemberName(selectedLog.fullName);
                      await fetchGuestsForMember(memberEmail);
                      setShowGuestModal(true);
                    } else {
                      toast.error("Could not link guest profile: Missing member email anchor.");
                    }
                  }}
                >
                  MANAGE GUESTS
                </button>
              )}
            </div>
          )}
          <button className="qr-btn qr-btn-warning w-100 mt-3" onClick={() => setShowDetailModal(false)}>Close</button>
        </Modal.Body>
      </Modal>

      {/* --- GUEST MANAGEMENT MODAL --- */}
      <Modal show={showGuestModal} onHide={() => setShowGuestModal(false)} centered contentClassName="qr-modal-content bg-dark-profile" backdropClassName='bg-dark'>
        <div className="qr-modal-success-bar" style={{ background: 'var(--brand-primary)' }}></div>
        <Modal.Body className="p-4 text-white">
          <h2 className="fw-bold mb-1 text-center">Guest Management</h2>
          <p className="qr-subtext small text-center mb-4 opacity-50">Sponsor: {formatDisplayValue(lastMemberName)}</p>
          <InputGroup className="mb-4"><Form.Control placeholder="Guest Name..." className="qr-input" value={guestNameInput} onChange={(e) => setGuestNameInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleQuickGuestAdd()} /><button className="qr-btn qr-btn-primary" onClick={handleQuickGuestAdd}>ADD</button></InputGroup>
          <div className="qr-card-list" style={{ maxHeight: '250px', overflowY: 'auto' }}>
            {guestItems.map((guest) => (
              <div key={guest.id} className="qr-log-card py-2 d-flex justify-content-between align-items-center mb-2">
                <div className="fw-bold small ps-2">{formatDisplayValue(guest.fullName)}</div>
                <button className="btn btn-sm text-danger fw-bold border-0 bg-transparent" onClick={() => { if (window.confirm("Remove guest?")) updateDoc(doc(db, getLogsCollectionName(), guest.id), { status: 'cancelled' }).then(() => fetchGuestsForMember(lastMemberEmail)).then(() => fetchLogs(!historyMode)) }}>REMOVE</button>
              </div>
            ))}
            {guestItems.length === 0 && <div className="text-center py-4 qr-subtext">No active guests</div>}
          </div>
          <button className="qr-btn qr-btn-warning w-100 mt-4" onClick={() => { setShowGuestModal(false); fetchLogs(!historyMode); }}>Close</button>
        </Modal.Body>
      </Modal>



      <style>{`
        .pos-relative { position: relative; }
        .outer-scanner-frame { }
        .qr-video-container {
            width: 100%;
            aspect-ratio: 1 / 1;
            max-height: 420px;
            background-color: #050505;
        }
        .qr-video-container div:not(.custom-scanner-overlay):not(.scanner-square-finder):not(.corner-bracket):not(.scanner-laser-line), 
        .qr-video-container svg {
            border: none !important;
            box-shadow: none !important;
        }
        .custom-scanner-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            background: radial-gradient(circle, rgba(0,0,0,0.1) 40%, rgba(220,53,69,0.12) 85%, rgba(0,0,0,0.65) 100%);
            pointer-events: none;
            z-index: 10;
        }
        .scanner-square-finder {
            position: relative;
            width: 72%;
            height: 72%;
            max-width: 280px;
            max-height: 280px;
            aspect-ratio: 1 / 1;
            background: transparent;
        }
        .corner-bracket {
            position: absolute;
            width: 24px;
            height: 24px;
            border: 4px dashed #ff3b30;
            filter: drop-shadow(0px 0px 6px rgba(255, 59, 48, 0.8));
        }
        .corner-bracket.tl { top: 0; left: 0; border-right: none; border-bottom: none; border-top-left-radius: 4px; }
        .corner-bracket.tr { top: 0; right: 0; border-left: none; border-bottom: none; border-top-right-radius: 4px; }
        .corner-bracket.bl { bottom: 0; left: 0; border-right: none; border-top: none; border-bottom-left-radius: 4px; }
        .corner-bracket.br { bottom: 0; right: 0; border-left: none; border-top: none; border-bottom-right-radius: 4px; }
        
        .scanner-laser-line {
            position: absolute;
            left: 4%;
            right: 4%;
            height: 3px;
            background: linear-gradient(90deg, transparent 5%, #ff3b30 50%, transparent 95%);
            box-shadow: 0 0 12px #ff30c1, 0 0 22px rgba(255, 48, 193, 0.86);
            opacity: 0.95;
            animation: scanMotion 2s cubic-bezier(0.445, 0.05, 0.55, 0.95) infinite;
        }
        @keyframes scanMotion {
            0% { top: 6%; transform: scaleX(0.96); opacity: 0.8; }
            50% { top: 92%; transform: scaleX(1.02); opacity: 1; }
            100% { top: 6%; transform: scaleX(0.96); opacity: 0.8; }
        }
        .custom-scroll::-webkit-scrollbar {
            width: 4px;
        }
        .custom-scroll::-webkit-scrollbar-track {
            background: rgba(255,255,255,0.02);
        }
        .custom-scroll::-webkit-scrollbar-thumb {
            background: rgba(255,255,255,0.1);
            border-radius: 2px;
        }

        /* Tighten spacing inside Guest Management lists */
        .qr-modal-body-container .qr-card-list .qr-log-card {
            margin-bottom: 4px !important;   /* Drops gap down between items */
            padding-top: 6px !important;      /* Less top breathing room */
            padding-bottom: 6px !important;   /* Less bottom breathing room */
        }

        /* Brings the input field closer to the guest rows */
        .qr-modal-body-container .qr-guest-section {
            margin-bottom: 12px !important; 
        }

        /* Mobile Responsive Layout Adaptation */
        .qr-card-content {
            display: flex;
            width: 100%;
        }
        .qr-card-main-info {
            flex: 1;
            min-width: 0; /* Critical flex wrapper override for text containment */
            overflow-wrap: break-word;
            word-wrap: break-word;
        }
        
        /* Force names to wrap completely on small screens instead of clipping with ellipses (...) */
        .qr-card-list .qr-card-name {
            white-space: normal !important;
            text-overflow: clip !important;
            overflow: visible !important;
            word-break: break-word !important;
        }

        /* Clean up indentation look on role status indicators */
        .qr-card-list .qr-badge {
            padding-left: 0 !important;
            margin-left: 0 !important;
            background: transparent !important;
            box-shadow: none !important;
            border: none !important;
        }

        .action-tray-container {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 0 4px;
            flex-shrink: 0; /* Protect layout structure width on mobile viewports */
        }
        .qr-card-time-wrap {
            flex-shrink: 0; /* Protect layout structure width on mobile viewports */
        }

        /* Dynamic Row Action Interventions Styling */
        .action-circle-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 26px;
            height: 26px;
            border-radius: 50%;
            border: 1px solid rgba(255, 255, 255, 0.2);
            background-color: rgba(255, 255, 255, 0.07);
            color: #fff;
            cursor: pointer;
            transition: all 0.2s ease-in-out;
            padding: 0;
        }
        .action-circle-btn:hover {
            transform: scale(1.1);
            border-color: transparent;
        }
        
        /* Danger-themed Ban Buttons */
        .action-circle-btn.ban-btn {
            color: var(--brand-secondary, #dc3545);
            border-color: rgba(220, 53, 69, 0.4);
        }
        .action-circle-btn.ban-btn:hover {
            background-color: var(--brand-secondary, #dc3545);
            color: #fff !important;
        }
        .action-circle-btn.ban-btn:hover svg {
            fill: #fff !important;
            color: #fff !important;
        }
        
        /* Warning-themed Incident Buttons */
        .action-circle-btn.incident-btn {
            color: var(--brand-warning, #ffc107);
            border-color: rgba(255, 193, 7, 0.4);
        }
        .action-circle-btn.incident-btn:hover {
            background-color: var(--brand-warning, #ffc107);
            color: #000 !important;
        }
        
        /* Primary success-themed Guest Buttons */
        .action-circle-btn.guest-btn {
            color: var(--brand-primary, #198754);
            border-color: rgba(25, 135, 84, 0.4);
        }
        .action-circle-btn.guest-btn:hover {
            background-color: var(--brand-primary, #198754);
            color: #fff !important;
        }
        .action-circle-btn.guest-btn:hover svg {
            fill: #fff !important;
            color: #fff !important;
        }
      `}</style>
    </div>
  );
};

export default QRWebAndNativeScan;